import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Bot,
  Send,
  Sparkles,
  FileText,
  ClipboardList,
  Shield,
  User,
  Loader2,
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const quickPrompts = [
  { id: 'compliance', text: 'How can I improve my CQC compliance score?', icon: Shield },
  { id: 'safeguarding', text: 'What are the key safeguarding requirements?', icon: AlertTriangle },
  { id: 'documentation', text: 'Best practices for care documentation', icon: FileText },
  { id: 'staff', text: 'Staff supervision and training requirements', icon: User },
];

const contextTypes = [
  { id: 'general', name: 'General CQC Advice', description: 'General compliance guidance' },
  { id: 'compliance', name: 'Compliance Analysis', description: 'Analyze your organization data' },
  { id: 'care_plan', name: 'Care Planning', description: 'Person-centred care plan guidance' },
  { id: 'hr', name: 'HR & Staffing', description: 'Employment and staffing advice' },
];

export default function AIAssistant() {
  const { user, organization } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [contextType, setContextType] = useState('general');
  const [includeOrgData, setIncludeOrgData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ai/ask`, {
        query: message,
        context_type: contextType,
        include_org_data: includeOrgData,
      });

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        recommendations: response.data.recommendations,
        references: response.data.references,
        confidence: response.data.confidence,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again or check if the AI service is configured correctly.',
        isError: true,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const fetchComplianceSummary = async () => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai/compliance-summary`);
      setComplianceSummary(response.data);
    } catch (error) {
      console.error('Error fetching compliance summary:', error);
      toast.error('Failed to generate compliance summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen" data-testid="ai-assistant-page">
      <Header title="AI Compliance Assistant" />

      <div className="p-6 space-y-6">
        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                <Bot className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-teal-900">Powered by Gemini AI</h3>
                <p className="text-sm text-teal-700 mt-1">
                  Your AI-powered CQC compliance consultant. Ask questions about regulations, 
                  get personalized recommendations based on your organization&apos;s data, and receive 
                  guidance on maintaining outstanding care standards.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="summary">Compliance Summary</TabsTrigger>
            <TabsTrigger value="tools">AI Tools</TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Context Mode</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Select value={contextType} onValueChange={setContextType}>
                      <SelectTrigger data-testid="context-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {contextTypes.map(ct => (
                          <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {contextTypes.find(ct => ct.id === contextType)?.description}
                    </p>
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="includeOrgData"
                        checked={includeOrgData}
                        onChange={(e) => setIncludeOrgData(e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="includeOrgData" className="text-sm">Include organization data</label>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Quick Prompts</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {quickPrompts.map(prompt => (
                      <Button
                        key={prompt.id}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left h-auto py-2"
                        onClick={() => sendMessage(prompt.text)}
                        disabled={loading}
                        data-testid={`quick-prompt-${prompt.id}`}
                      >
                        <prompt.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="text-xs">{prompt.text}</span>
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Chat Area */}
              <div className="lg:col-span-3">
                <Card className="h-[600px] flex flex-col">
                  <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                      <Bot className="w-5 h-5 text-teal-600" />
                      <CardTitle className="text-base">CQC Compliance Assistant</CardTitle>
                      <Badge variant="outline" className="ml-auto text-xs">
                        <Sparkles className="w-3 h-3 mr-1" /> Gemini 3 Flash
                      </Badge>
                    </div>
                  </CardHeader>

                  {/* Messages */}
                  <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center text-slate-400">
                        <MessageSquare className="w-12 h-12 mb-4" />
                        <p className="font-medium">Start a conversation</p>
                        <p className="text-sm mt-1">Ask anything about CQC compliance, care standards, or regulations</p>
                      </div>
                    ) : (
                      messages.map(message => (
                        <div
                          key={message.id}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                            message.role === 'user'
                              ? 'bg-teal-600 text-white'
                              : message.isError
                              ? 'bg-red-50 border border-red-200'
                              : 'bg-slate-100'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                            
                            {message.recommendations?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-200">
                                <p className="text-xs font-medium mb-2 flex items-center gap-1">
                                  <Lightbulb className="w-3 h-3" /> Recommendations
                                </p>
                                <ul className="text-xs space-y-1">
                                  {message.recommendations.slice(0, 3).map((rec, i) => (
                                    <li key={i} className="flex items-start gap-1">
                                      <CheckCircle className="w-3 h-3 mt-0.5 text-teal-600" />
                                      {rec}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {message.confidence && (
                              <p className="text-xs mt-2 opacity-70">
                                Confidence: {message.confidence}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-slate-100 rounded-2xl px-4 py-3">
                          <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </CardContent>

                  {/* Input Area */}
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about CQC compliance, care standards, or regulations..."
                        className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                        disabled={loading}
                        data-testid="chat-input"
                      />
                      <Button
                        onClick={() => sendMessage()}
                        disabled={loading || !inputMessage.trim()}
                        className="bg-teal-600 hover:bg-teal-700 self-end"
                        data-testid="send-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Compliance Summary Tab */}
          <TabsContent value="summary" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>AI-Generated Compliance Summary</CardTitle>
                    <CardDescription>Get an AI analysis of your organization&apos;s compliance status</CardDescription>
                  </div>
                  <Button
                    onClick={fetchComplianceSummary}
                    disabled={loadingSummary}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="generate-summary-btn"
                  >
                    {loadingSummary ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Summary
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {complianceSummary ? (
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <pre className="text-sm whitespace-pre-wrap">{complianceSummary.summary}</pre>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>Generated: {new Date(complianceSummary.generated_at).toLocaleString()}</span>
                      <span>•</span>
                      <span>Data analyzed: {complianceSummary.data_analyzed?.join(', ')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Click &quot;Generate Summary&quot; to get an AI-powered analysis</p>
                    <p className="text-sm text-slate-400 mt-1">
                      The AI will analyze your CQC scores, incidents, audits, and more
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Document Analyzer</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Upload and analyze care documents for CQC compliance
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Care Plan Generator</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Generate AI-powered care plan suggestions
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Policy Reviewer</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Review policies against CQC requirements
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
